import * as React from 'react'
import styled, { css } from 'styled-components'
import { color, fontSize, transition } from 'utils'
import { theme } from 'utils/styledHelper'

import injectE2E from './Legacy/injectE2E'
import { ButtonSizeType, ColorType, ThemePropsType as _T } from './types'

type HtmlButtonType = 'button' | 'reset' | 'submit'

type PropsType = Omit<React.HTMLProps<HTMLButtonElement>, 'size'> & {
  bounced?: boolean
  children?: React.ReactNode
  disabled?: boolean
  e2e?: string
  size?: ButtonSizeType // FIXME ButtonSizeType errors when using typed styled-components
  type?: HtmlButtonType
}

const Button = styled.button.attrs<PropsType>(injectE2E)<PropsType>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  height: 2.5rem;
  line-height: 2.5rem;
  width: 100%;
  padding: 0 1rem;
  box-sizing: border-box;
  color: ${theme('buttonColor')};
  background-color: ${theme('buttonBackgroundColor')};
  border: none;
  border-radius: ${theme('buttonBorderRadius')};
  cursor: ${(props) => (props.disabled === true ? 'not-allowed' : 'pointer')};
  outline: none;
  transition: all 0.3s ease 0s;
  opacity: ${(props) => (props.disabled === true ? 0.5 : 1)};
  font-family: ${theme('fontPrimary')};
  font-size: ${fontSize('md')};
  font-weight: 600;
  &:hover {
    background-color: ${(props) =>
      props.disabled === true
        ? theme('buttonBackgroundColor')(props)
        : theme('buttonHoverColor')(props)};
    transform: ${(props) => (props.bounced === true ? 'scale(0.95)' : 'none')};
  }
`

const plainButtonCss = css<PropsType & _T>`
  width: auto;
  background: transparent;
  height: ${(props) => props.theme.buttonSizes[props.size ?? 'lg']};
  line-height: ${fontSize('md')};
  font-weight: 600;

  &:hover:not([disabled]) {
    text-decoration: underline;
    background: transparent;
  }
`

const PlainButton = styled(Button)`
  ${plainButtonCss};
`

const base = (
  colorCode: ColorType,
  darkColorCode: ColorType,
  textColorCode: ColorType = 'white'
) => css`
  width: auto;
  background: ${colorCode ? color(colorCode) : 'transparent'};
  color: ${color(textColorCode)};
  line-height: ${fontSize('md')};
  height: ${(props: PropsType & _T) => props.theme.buttonSizes[props.size ?? 'lg']};

  &:disabled {
    background: ${color(colorCode)};
  }

  &:hover:not([disabled]) {
    background: ${color(darkColorCode)};
  }
`

const BlueGrayButton = styled(Button)`
  ${base('grey800', 'grey900')};
`

const BorderButton = styled(PlainButton)`
  box-shadow: inset 0 0 0 2px ${color('white')};
`

const CeruleanButton = styled(Button)`
  ${base('blue600', 'darkCerulean')};
`

const LightGrayButton = styled(Button)`
  ${base('dark600', 'blue600', 'white')};
`

const GrayButton = styled(Button)`
  ${base('grey000', 'dark000', 'dark300')};
`

const GrayDarkButton = styled(Button)`
  ${base('whiteAlpha10', 'whiteAlpha25', 'white')};
`

const GoldButton = styled(Button)`
  ${base('tierGold', 'darkGold', 'dark800')};
`

const OrientButton = styled(Button)`
  ${base('orient', 'darkOrient')};
`

const WhiteButton = styled(Button)`
  ${base('white', 'dark000', 'grey800')};
`

const WhiteBlueButton = styled(Button)`
  ${base('white', 'dark000', 'blue600')};
`

const SideButton = styled(Button)`
  ${base('overlayDark800', 'overlayDark600')};
  transition: background ${transition('fast')} ease-out;
`

const RedButton = styled(Button)`
  ${base('red600', 'red700')};
`

const MediumBorderButton = styled(BorderButton)`
  font-size: ${fontSize('sm')};
  box-shadow: inset 0 0 0 1px ${color('dark400')};
  padding: 0.75rem;
  border-radius: 8px;
  height: 2rem;
  text-decoration: none !important;
  transition: all 0.1s ease-in;

  &:hover {
    background: ${color('dark800')} !important;
  }
`

const MediumBorderButtonBlue = styled(MediumBorderButton)`
  color: ${color('blue400')};
`

const MediumBorderButtonRed = styled(MediumBorderButton)`
  color: ${color('red400')};
`

const buttonBarCss = css`
  button {
    flex: 1;
  }

  button:not(:last-child) {
    margin-right: 1rem;
  }
`

const buttonPipeCss = css`
  button:not(:last-child) {
    margin-bottom: 1rem;
  }
`

export {
  BlueGrayButton,
  buttonBarCss,
  buttonPipeCss,
  CeruleanButton,
  GoldButton,
  GrayButton,
  GrayDarkButton,
  LightGrayButton,
  MediumBorderButtonBlue,
  MediumBorderButtonRed,
  OrientButton,
  PlainButton,
  plainButtonCss,
  RedButton,
  SideButton,
  WhiteBlueButton,
  WhiteButton
}
